import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ChapterDescription, type ChapterNote } from "../api";

interface Props {
  bookId: number;
  chapters: ChapterDescription[] | null;
}

export function ChapterNotesSection({ bookId, chapters }: Props) {
  const qc = useQueryClient();
  const [spoilerSafe, setSpoilerSafe] = useState(true);
  const [openChapter, setOpenChapter] = useState<number | null>(null);

  const list = useQuery({
    queryKey: ["notes", bookId, spoilerSafe],
    queryFn: () =>
      api.get<{ notes: ChapterNote[] }>(
        `/notes/${bookId}?spoilerSafe=${spoilerSafe ? "1" : "0"}`
      ),
  });

  const create = useMutation({
    mutationFn: (payload: { chapter: number; body: string }) =>
      api.post<{ note: ChapterNote }>("/notes", { ...payload, bookId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", bookId] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/notes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", bookId] }),
  });

  const notes = list.data?.notes ?? [];

  // Build chapter list to render. If we have AI chapters, use those; otherwise
  // derive from the notes we have.
  const chapterList = useMemo(() => {
    if (chapters && chapters.length > 0) {
      return chapters.map((c) => ({ number: c.number, title: c.title }));
    }
    const seen = new Map<number, { number: number; title: string | null }>();
    notes.forEach((n) => {
      if (!seen.has(n.chapter)) seen.set(n.chapter, { number: n.chapter, title: null });
    });
    return Array.from(seen.values()).sort((a, b) => a.number - b.number);
  }, [chapters, notes]);

  const notesByChapter = useMemo(() => {
    const map = new Map<number, ChapterNote[]>();
    for (const n of notes) {
      const list = map.get(n.chapter) ?? [];
      list.push(n);
      map.set(n.chapter, list);
    }
    return map;
  }, [notes]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-plum-soft text-sm leading-relaxed max-w-md">
          Leave a thought tied to a chapter. By default, you'll only see notes from chapters
          you've already read — no spoilers.
        </p>
        <button
          onClick={() => setSpoilerSafe((v) => !v)}
          className={[
            "text-[0.7rem] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full transition-colors shrink-0",
            spoilerSafe
              ? "bg-cream-100 text-plum-soft hover:bg-cream-200"
              : "bg-rose text-white",
          ].join(" ")}
          title={spoilerSafe ? "Click to show every note" : "Click to hide spoiler notes"}
        >
          {spoilerSafe ? "Spoiler-safe" : "Showing all"}
        </button>
      </div>

      <div className="flex flex-col divide-y divide-cream-200">
        {chapterList.map((ch) => {
          const chNotes = notesByChapter.get(ch.number) ?? [];
          const isOpen = openChapter === ch.number;
          return (
            <div key={ch.number} className="py-2">
              <button
                onClick={() => setOpenChapter(isOpen ? null : ch.number)}
                className="w-full flex items-center gap-4 py-3 text-left hover:bg-cream-50/60 rounded-lg px-2 transition-colors"
              >
                <span className="font-serif italic text-rose text-2xl w-10 text-center shrink-0">
                  {ch.number}
                </span>
                <div className="flex-1 min-w-0">
                  {ch.title ? (
                    <div className="font-serif text-lg text-plum">{ch.title}</div>
                  ) : (
                    <div className="font-serif text-lg text-plum">Chapter {ch.number}</div>
                  )}
                </div>
                {chNotes.length > 0 ? (
                  <span className="text-[0.7rem] uppercase tracking-[0.14em] text-plum-mute shrink-0">
                    {chNotes.length} {chNotes.length === 1 ? "note" : "notes"}
                  </span>
                ) : null}
                <span
                  className={[
                    "transition-transform duration-200 text-plum-mute",
                    isOpen ? "rotate-90" : "",
                  ].join(" ")}
                >
                  ›
                </span>
              </button>

              {isOpen ? (
                <div className="pl-12 pr-2 pb-5 pt-2 flex flex-col gap-3">
                  {chNotes.length === 0 ? (
                    <p className="text-plum-mute italic text-sm">No notes yet.</p>
                  ) : null}
                  {chNotes.map((n) => (
                    <NoteCard
                      key={n.id}
                      note={n}
                      onDelete={() => remove.mutate(n.id)}
                    />
                  ))}
                  <NoteComposer
                    onSubmit={(body) => create.mutate({ chapter: ch.number, body })}
                    pending={create.isPending}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
        {chapterList.length === 0 ? (
          <p className="text-plum-mute italic py-6 text-center">
            Generate the chapter walkthrough first, or add a note to start the conversation.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function NoteCard({ note, onDelete }: { note: ChapterNote; onDelete: () => void }) {
  const initial = (note.author.name ?? note.author.email).trim().charAt(0).toUpperCase();
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blush to-rose-light text-white flex items-center justify-center font-serif text-sm shrink-0">
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-plum text-sm">
            {note.author.name ?? note.author.email.split("@")[0]}
          </span>
          {note.isMine ? (
            <span className="text-[0.65rem] uppercase tracking-[0.14em] text-gold-dark">You</span>
          ) : null}
          <span className="text-xs text-plum-mute">
            {new Date(note.createdAt).toLocaleDateString()}
          </span>
        </div>
        <p className="text-plum-soft text-sm leading-relaxed mt-1 whitespace-pre-wrap">
          {note.body}
        </p>
      </div>
      {note.isMine ? (
        <button
          onClick={onDelete}
          className="text-[0.65rem] uppercase tracking-[0.14em] text-plum-mute hover:text-rose self-start"
        >
          Delete
        </button>
      ) : null}
    </div>
  );
}

function NoteComposer({ onSubmit, pending }: { onSubmit: (body: string) => void; pending: boolean }) {
  const [text, setText] = useState("");
  return (
    <div className="flex flex-col gap-2 mt-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Leave a thought…"
        rows={2}
        className="w-full px-3 py-2 rounded-lg bg-cream-50 border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic resize-none focus:outline-none focus:border-rose"
      />
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (text.trim()) {
              onSubmit(text.trim());
              setText("");
            }
          }}
          disabled={!text.trim() || pending}
          className="text-xs uppercase tracking-[0.14em] px-4 py-1.5 rounded-full bg-rose text-white hover:bg-rose-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {pending ? "Saving…" : "Add note"}
        </button>
      </div>
    </div>
  );
}
